import { useState, useRef } from 'react'
import clsx from 'clsx'
import { XMarkIcon } from '@heroicons/react/24/outline'

export interface FormMultiSelectOption {
  value: string | number
  label: string
}

interface FormMultiSelectProps {
  values: (string | number)[]
  options: FormMultiSelectOption[]
  onChange: (values: (string | number)[]) => void
  allowCreate?: boolean
  placeholder?: string
}

export default function FormMultiSelect({ values, options, onChange, allowCreate = false, placeholder }: FormMultiSelectProps) {
  const [query, setQuery] = useState('')
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const available = options.filter((o) => !values.includes(o.value))
  const filtered = query
    ? available.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : available
  const canCreate = allowCreate && query.trim().length > 0 && filtered.length === 0
  const showDropdown = focused && query.length > 0 && (filtered.length > 0 || canCreate)

  const remove = (val: string | number) => {
    onChange(values.filter((v) => v !== val))
  }

  const add = (val: string | number) => {
    const parsed = typeof val === 'string' && !isNaN(Number(val)) ? Number(val) : val
    onChange([...values, parsed])
    setQuery('')
    inputRef.current?.focus()
  }

  return (
    <div className="relative">
      <span
        data-slot="control"
        className={clsx(
          'relative block w-full',
          'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm',
          'dark:before:hidden',
        )}
      >
        <div
          className={clsx(
            'relative flex flex-wrap items-center gap-1 rounded-lg border border-zinc-950/10 bg-transparent px-2',
            'py-[calc(--spacing(1.5)-1px)] sm:py-[calc(--spacing(1)-1px)]',
            'dark:border-white/10 dark:bg-white/5',
          )}
          onClick={() => inputRef.current?.focus()}
        >
          {values.map((val) => {
            const opt = options.find((o) => String(o.value) === String(val))
            return (
              <span key={val} className="inline-flex items-center gap-0.5 rounded bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400">
                {opt?.label || val}
                <button type="button" onClick={(e) => { e.stopPropagation(); remove(val) }} className="hover:text-indigo-900 dark:hover:text-white">
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </span>
            )
          })}
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim()) {
                e.preventDefault()
                if (filtered.length > 0) {
                  add(filtered[0].value)
                } else if (allowCreate) {
                  add(query.trim())
                }
              }
              if (e.key === 'Backspace' && query === '' && values.length > 0) {
                remove(values[values.length - 1])
              }
            }}
            placeholder={values.length === 0 ? (placeholder || 'Type to search...') : ''}
            className="min-w-[60px] flex-1 border-0 bg-transparent p-0 text-xs/5 text-zinc-950 placeholder:text-zinc-500 focus:outline-none dark:text-white"
          />
        </div>
      </span>
      {showDropdown && (
        <div className="absolute left-0 z-50 mt-1 max-h-48 w-full overflow-y-auto rounded-xl bg-white/75 p-1 shadow-lg ring-1 ring-zinc-950/10 backdrop-blur-xl dark:bg-zinc-800/75 dark:ring-white/10">
          {filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(o.value)}
              className="block w-full rounded-lg px-2.5 py-1 text-left text-xs/5 text-zinc-950 hover:bg-indigo-500 hover:text-white dark:text-white"
            >
              {o.label}
            </button>
          ))}
          {canCreate && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(query.trim())}
              className="block w-full rounded-lg px-2.5 py-1 text-left text-xs/5 text-indigo-600 hover:bg-indigo-500 hover:text-white dark:text-indigo-400"
            >
              Create &ldquo;{query.trim()}&rdquo;
            </button>
          )}
        </div>
      )}
    </div>
  )
}
